// @ts-nocheck
import React, {useEffect, useMemo, useState} from 'react'
import {AgGridReact} from '@ag-grid-community/react'

import api from '../../controlRequests/api/api'
import GraphDayDetailsModal from '../../controlRequests/components/GraphDayDetailsModal'
import {
    getGraphDayLabel,
    getGraphDayState,
    getReasonStripeBackground,
    hasGraphDayDetails,
} from '../../controlRequests/utils/graphDay'


const GraphWellHistoryModal = ({row, onClose}) => {
    const [months, setMonths] = useState('all')
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [dayDetails, setDayDetails] = useState(null)

    useEffect(() => {
        if (!row?.wells?.id) return

        setLoading(true)
        setError('')
        api.get('well-history/', {
            params: {
                well: row.wells.id,
                months,
            },
        })
            .then((response) => setRows(response.data?.results || []))
            .catch((requestError) => {
                console.error(requestError)
                setRows([])
                setError(requestError.response?.data?.detail || 'Не удалось загрузить историю графика')
            })
            .finally(() => setLoading(false))
    }, [row?.wells?.id, months])

    const columnDefs = useMemo(() => {
        const dayColumns = Array.from({length: 31}, (_, index) => {
            const day = index + 1
            return {
                field: `day_${day}`,
                headerName: String(day),
                width: 36,
                minWidth: 32,
                maxWidth: 40,
                headerClass: 'graph-history-day-header',
                cellClass: 'graph-history-day-cell',
                sortable: false,
                filter: false,
                resizable: false,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                suppressHeaderContextMenu: true,
                valueGetter: (params) => {
                    if (day > Number(params.data?.days_in_month || 31)) return null
                    return params.data?.days?.[String(day)] || null
                },
                cellRenderer: (params) => {
                    const isCalendarDay = day <= Number(params.data?.days_in_month || 31)
                    return (
                        <div style={getHistoryDayStyle(params.value, isCalendarDay)}>
                            {isCalendarDay ? getGraphDayLabel(params.value) : ''}
                        </div>
                    )
                },
                onCellClicked: (params) => {
                    if (!hasGraphDayDetails(params.value)) return
                    setDayDetails({
                        value: params.value,
                        wellName: row?.wells?.name,
                        monthLabel: params.data?.month_label,
                        day,
                    })
                },
            }
        })

        return [
            {field: 'month_label', headerName: 'Месяц', width: 78, pinned: 'left'},
            {
                field: 'org_name',
                headerName: 'Подрядчик',
                width: 170,
                minWidth: 130,
                pinned: 'left',
            },
            {field: 'plan', headerName: 'План', width: 58, pinned: 'left'},
            {field: 'fact', headerName: 'Факт', width: 58, pinned: 'left'},
            {field: 'level', headerName: 'Степень', width: 90, pinned: 'left'},
            ...dayColumns,
        ]
    }, [row?.wells?.name])

    return (
        <div className="graph-history-overlay" onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose()
        }}>
            <div className="graph-history-modal">
                <div className="graph-history-modal-header">
                    <div>
                        <h2>Скважина {row?.wells?.name}</h2>
                        <span>
                            Куст {row?.bush || '—'} · {row?.deposit || '—'}
                        </span>
                    </div>
                    <div className="graph-history-modal-controls">
                        <label>
                            <span>Период</span>
                            <select value={months} onChange={(event) => setMonths(event.target.value)}>
                                <option value="5">5 месяцев</option>
                                <option value="12">12 месяцев</option>
                                <option value="24">24 месяца</option>
                                <option value="all">Вся история</option>
                            </select>
                        </label>
                        <button type="button" onClick={onClose} aria-label="Закрыть">×</button>
                    </div>
                </div>

                {error && <div className="graph-history-error">{error}</div>}

                <div className="ag-theme-alpine graph-history-table">
                    <AgGridReact
                        rowData={rows}
                        columnDefs={columnDefs}
                        rowHeight={26}
                        headerHeight={28}
                        defaultColDef={{
                            sortable: false,
                            filter: false,
                            floatingFilter: false,
                            resizable: false,
                            suppressMovable: true,
                            suppressHeaderMenuButton: true,
                            suppressHeaderFilterButton: true,
                            suppressHeaderContextMenu: true,
                        }}
                        overlayNoRowsTemplate={loading ? 'Загружаем график...' : 'История графика отсутствует'}
                    />
                </div>
            </div>
            <GraphDayDetailsModal details={dayDetails} onClose={() => setDayDetails(null)} />
        </div>
    )
}

function getHistoryDayStyle(value, isCalendarDay) {
    const base = {
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        fontWeight: 700,
        cursor: hasGraphDayDetails(value) ? 'pointer' : 'default',
    }
    if (!isCalendarDay) return {...base, background: '#d0d5dd', color: '#667085'}
    if (!value || typeof value !== 'object') return {...base, background: '#ffffff'}

    const state = getGraphDayState(value)
    const styles = {
        done_planned: {background: '#81c784', color: '#1b5e20'},
        done: {background: '#81c784', color: '#1b5e20'},
        done_outside_plan: {
            background: 'linear-gradient(to bottom right, #81c784 0 calc(50% - 0.5px), #fff calc(50% + 0.5px) 100%)',
            color: '#1b5e20',
        },
        manual_planned: {background: '#2e7d32', color: '#ffffff'},
        manual_outside_plan: {
            background: 'linear-gradient(to bottom right, #2e7d32 0 calc(50% - 0.5px), #fff calc(50% + 0.5px) 100%)',
            color: '#1b5e20',
        },
        no_pass: {background: '#fff176', color: '#795548'},
        ppk_pending: {
            background: 'linear-gradient(to bottom right, #fff176 0 calc(50% - 0.5px), #66bb6a calc(50% + 0.5px) 100%)',
            color: '#263238',
        },
        reason: {
            background: getReasonStripeBackground(state.reason),
            color: '#475467',
        },
        planned: {background: '#b3e5fc', color: '#01579b', fontWeight: 500},
    }
    return {...base, ...(styles[state.kind] || {background: '#ffffff', color: '#344054'})}
}

export default GraphWellHistoryModal
